"use client";

import { api } from "@convex/_generated/api";
import { useMutation } from "convex/react";
import { useCallback, useEffect, useState } from "react";
import {
  getAnonymousVoteToken,
  setAnonymousVoteToken,
} from "@/lib/anonymous-vote-token";

/**
 * Custom hook to manage anonymous voting
 * This hook ensures that anonymous users have a token before they can vote
 */
export function useAnonymousVote() {
  const [token, setToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Mutation to get or create an anonymous token
  const getOrCreateToken = useMutation(
    api.anonymousVotes.mutations.getOrCreateAnonymousToken
  );

  // Initialize token on mount
  useEffect(() => {
    const initializeToken = async () => {
      // Check if we already have a token in cookies
      const existingToken = getAnonymousVoteToken();

      if (existingToken) {
        setToken(existingToken);
        setIsInitialized(true);
        return;
      }

      // If no token exists, create one
      try {
        const newToken = await getOrCreateToken({
          existingToken: undefined,
        });
        setAnonymousVoteToken(newToken);
        setToken(newToken);
      } catch {
        // Silently fail - voting won't be available for anonymous users
      } finally {
        setIsInitialized(true);
      }
    };

    initializeToken();
  }, [getOrCreateToken]);

  // Function to ensure we have a token before performing an action
  const ensureToken = useCallback(async (): Promise<string> => {
    if (token) {
      return token;
    }

    // If no token exists, create one
    const existingToken = getAnonymousVoteToken();
    if (existingToken) {
      setToken(existingToken);
      return existingToken;
    }

    const newToken = await getOrCreateToken({
      existingToken: undefined,
    });
    setAnonymousVoteToken(newToken);
    setToken(newToken);
    return newToken;
  }, [token, getOrCreateToken]);

  return {
    token,
    isInitialized,
    ensureToken,
  };
}
